import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

function CustomSearchableCheckboxDropdown({
    items = [],
    value,
    labelKey,
    valueKey,
    placeholder = "Select",
    onChange,
    searchable = true,
    disabled = false,
    multiple = false,
    showSelectAll = false,   // NEW PROP
    required = false,
    error = false  
}) {
    const { t } = useTranslation();
    const [touched, setTouched] = useState(false);

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dropdownRef = useRef(null);

    const selectedItem =
        !multiple && items.find((item) => item[valueKey] === value);

    const selectedItems = multiple
        ? items.filter((item) => (value || []).includes(item[valueKey]))
        : [];

    const filteredItems = useMemo(() => {
        if (!searchable) return items;
        return items.filter((item) =>
            item[labelKey]
                ?.toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())
        );
    }, [items, searchText, searchable, labelKey]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchText("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (item) => {
        setTouched(true);
        if (!multiple) {
            onChange(item[valueKey]);
            setIsOpen(false);
            setSearchText("");
            return;
        }

        let newValues = [...(value || [])];

        if (newValues.includes(item[valueKey])) {
            newValues = newValues.filter((v) => v !== item[valueKey]);
        } else {
            newValues.push(item[valueKey]);
        }

        onChange(newValues);
    };

    // SELECT ALL HANDLER
    const handleSelectAll = () => {
        setTouched(true);
        if ((value || []).length === items.length) {
            onChange([]);
        } else {
            const allValues = items.map((item) => item[valueKey]);
            onChange(allValues);
        }
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prev) => !prev);
            setSearchText("");
        }
    };

    const selectedLabel = () => {
        if (multiple) {
            const count = selectedItems.length;

            if (count === 0) return placeholder;
            if (count === 1) return selectedItems[0][labelKey];

            return `${count} ${t("current")}`; // e.g. "3 selected"
        }

        return selectedItem ? selectedItem[labelKey] : placeholder;
    };

    const allSelected = items.length > 0 && (value || []).length === items.length;

    const isValid = multiple
        ? (value || []).length > 0
        : !!value;

   const statusClass = error
    ? "error-form"
    : touched && isValid
        ? "ctm-success"
        : "";


    return (
        <div
            className="ctm-dropdown-inner d-block position-relative"
            ref={dropdownRef}
        >
            <a
                className={`ctm-dropdown 
                ${isOpen ? "dropdown-open" : ""} 
                ${disabled ? "ctm-dropdown-disabled" : ""}
                 ${statusClass}
                `}
                onClick={toggleDropdown}
            >
                {selectedLabel()}
            </a>



            {isOpen && !disabled && (
                <div className="ctm-dropdown-panel">

                    {searchable && (
                        <div className="px-3 pt-3 pb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("click_to_search")}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}

                    <ul className="ctm-dropdown-menu">

                        {/* SELECT ALL OPTION */}
                        {multiple && showSelectAll && (
                            <li onClick={handleSelectAll}>
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        readOnly
                                        className="me-2 form-check-input"
                                    />
                                </div>
                                <a>Select All</a>
                            </li>
                        )}

                        {filteredItems.length > 0 ? (
                            filteredItems.map((item) => {

                                const checked = multiple
                                    ? (value || []).includes(item[valueKey])
                                    : value === item[valueKey];

                                return (
                                    <li
                                        key={item[valueKey]}
                                        onClick={() => handleSelect(item)}
                                    >
                                        {multiple && (
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    readOnly
                                                    className="me-2 form-check-input"
                                                />
                                            </div>
                                        )}

                                        <a>{item[labelKey]}</a>
                                    </li>
                                );
                            })
                        ) : (
                            <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                                {t("react_no_options_found")}
                            </div>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default CustomSearchableCheckboxDropdown;
